/**
 * Dynamically updates the browser favicon.
 *
 * Priority:
 *  1. Admin logo URL (if provided)
 *  2. Canvas-drawn colored circle with brand initial (fallback)
 *
 * @param logoUrl  The admin-uploaded logo URL, or null/undefined to draw the circle fallback.
 * @param color    Hex/CSS color for the circle fallback (defaults to --primary-color CSS var).
 * @param initial  Letter to draw in the circle center (defaults to 'B').
 */
export function updateFavicon(
  logoUrl?: string | null,
  color?: string,
  initial?: string
): void {
  let href: string;

  if (logoUrl) {
    href = logoUrl;
  } else {
    const resolvedColor =
      color ||
      getComputedStyle(document.documentElement)
        .getPropertyValue('--primary-color')
        .trim() ||
      '#3a9397';

    href = drawCircleFavicon(resolvedColor, initial || 'B');
  }

  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = logoUrl ? '' : 'image/png';
  link.href = href;
}

function drawCircleFavicon(color: string, letter: string): string {
  const size = 64; // higher res for retina
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Circle
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  // Initial letter
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${Math.round(size * 0.52)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(letter.toUpperCase(), size / 2, size / 2 + 1);

  return canvas.toDataURL('image/png');
}
